import React, { useEffect, useMemo, useState } from 'react';
import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Chip, ChipColorScheme } from '@amzn/stencil-react-components/chip';
import { Card } from '@amzn/stencil-react-components/card';
import { IconFocus } from '@amzn/stencil-react-components/icons';
import { Modal } from '@amzn/stencil-react-components/modal';
import { StatusIndicator, Status } from '@amzn/stencil-react-components/status-indicator';
import { useNavigate } from 'react-router-dom';
import { RequestHelpWorkflowRenderer } from './RequestHelpWorkflowRenderer';
import { useGlobalContext } from '../hooks';
import { useErrorHandler } from 'react-error-boundary';
import { RequestHelpAction } from '../types/backendService';
import { Spinner } from '@amzn/stencil-react-components/spinner';
import moment from 'moment';
import {
  Col,
  Flex,
  Spacer,
  View
} from '@amzn/stencil-react-components/layout';
import {
  Text,
  H1,
  H3,
  H4
} from '@amzn/stencil-react-components/text';
import { Expander } from '@amzn/stencil-react-components/expander';
import { useTranslation } from 'react-i18next';
import { isRequestHelpEnabledForWarehouse } from '../util/allow_lists';
import { metricSender } from '../util/metric_sender';

interface GroupedActions {
  openActions: RequestHelpAction[];
  expiredActions: RequestHelpAction[];
  closedActions: RequestHelpAction[];
  allActions: RequestHelpAction[];
}

enum RequestTab {
  OPEN = "Open",
  CLOSED = "Closed",
  EXPIRED = "Expired",
  ALL = "All",
}

interface StatusConfig {
  statusIndicator: Status;
  statusName: string;
}

export const RequestHelp = ({}) => {
  const { t } = useTranslation();
  const { backendServiceClient, userToken, userDetails } = useGlobalContext();
  const navigate = useNavigate();
  const handleError = useErrorHandler();

  const [requests, setRequests] = useState([] as RequestHelpAction[]);
  const [selectedRequest, setSelectedRequest] = useState(undefined as RequestHelpAction|undefined);
  const [selectedTab, setSelectedTab] = useState(RequestTab.OPEN);

  const [isLoading, setLoading] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);
  // We only want to emit a metric the first time a user expands a particular FAQ item in the session
  const [visitedFAQItems, setVisitedFAQItems] = useState(new Set<string>());

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const visitFAQItem = (faqItem: string) => {
    if (!visitedFAQItems.has(faqItem)) {
      metricSender.generateFAQMetrics(userDetails, faqItem);

      const newVisitedFAQItems = new Set<string>(visitedFAQItems);
      newVisitedFAQItems.add(faqItem);
      setVisitedFAQItems(newVisitedFAQItems);
    }
  }

  const requestHelpEnabled = isRequestHelpEnabledForWarehouse(userDetails.warehouseId);

  // Configuration for the status indicator to display for each action status
  const REQUEST_STATUS_CONFIGS: Record<string, StatusConfig> = {
    'open': {
      statusIndicator: Status.Waiting,
      statusName: t('open')
    },
    'pending': {
      statusIndicator: Status.Waiting,
      statusName: t('open')
    },
    'workInProgress': {
      statusIndicator: Status.Waiting,
      statusName: t('open')
    },
    'expired': {
      statusIndicator: Status.Unavailable,
      statusName: t('expired')
    },
    'closed': {
      statusIndicator: Status.Positive,
      statusName: t('closed')
    },
  };

  /**
   * Generate list of action details
   */
  const generateActionDetailContent = (actionDetails: Record<string, string>|undefined) => {
    if (actionDetails == undefined) {
      return <></>;
    }
    const map = new Map<string, string>(Object.entries(actionDetails));
    if (map.size == 0) {
      return <></>;
    }
    const listItems = Array.from(map.keys()).map((key: string, index: number) => {
      return (
        <li key={index}><strong>{key}</strong>: {map.get(key)}</li>
      );
    });

    return (
      <>
        <Text fontWeight="light">{t('requestInputs')}:</Text>
        <ul>
          {listItems}
        </ul>
      </>
    );
  }

  const populateRequests = () => {
    (async () => {
      try {
        setLoading(true);

        const output = await backendServiceClient.getRequestHelpActions(userToken, userDetails.login);
        setRequests(output.actions!);

        setLoading(false);
      } catch (error) {
        handleError(new Error(t('dataFailureMessage')));
      }
    })();
  }

  useEffect(() => {
    populateRequests();
  }, []);

  /**
   * Determine the background to use for the Chip of each tab
   */
  const backgroundForTab = (tab: RequestTab) => {
    return selectedTab === tab ? ChipColorScheme.DarkBackground : ChipColorScheme.Default
  }

  /**
   * Group requests according to status for easy display in the tabs
   */
  const groupedActions: GroupedActions = useMemo(() => {
    const openActions: RequestHelpAction[] = [];
    const expiredActions: RequestHelpAction[] = [];
    const closedActions: RequestHelpAction[] = [];
    const allActions: RequestHelpAction[] = [];

    for (const action of requests) {
      switch(action.status) {
        case 'open':
        case 'pending':
        case 'workInProgress':
          openActions.unshift(action);
          allActions.unshift(action);
          break;
        case 'expired':
          expiredActions.unshift(action);
          allActions.unshift(action);
          break;
        case 'closed':
          closedActions.unshift(action);
          allActions.unshift(action);
          break;
      }
    }

    return {
      openActions,
      expiredActions,
      closedActions,
      allActions,
    }
  }, [requests]);

  /**
   * Unique Open Action Titles
   * Used to disable workflows to prevent the user from creating the same action when one is already open
   */
  const openActionTitles = new Set<string>(groupedActions.openActions.map((action: RequestHelpAction) => action.title!));

  /**
   * Render all actions for the current tab into cards
   */
  const actionCards = useMemo(() => {
    let requests = [];
    switch(selectedTab) {
      case RequestTab.OPEN:
        requests = groupedActions.openActions;
        break;
      case RequestTab.CLOSED:
        requests = groupedActions.closedActions;
        break;
      case RequestTab.EXPIRED:
        requests = groupedActions.expiredActions;
        break;
      case RequestTab.ALL:
        requests = groupedActions.allActions;
        break;
    }

    if (requests.length == 0) {
      return (
        <Text fontWeight="light">
          {t('noRequestsExist')}
        </Text>
      );
    }

    return requests.map((request: RequestHelpAction, index: number) => {
      const createdAt = moment(request.createdAt);
      const resolvedAt = moment(request.lastResolvedAt);

      return (
        <Card key={index}>
          <Col width="100%">
            <Text fontWeight="bold">{request.title}</Text>
            <Spacer height={8}/>
            <Text fontWeight="light">
              <em>
                {t('submittedAtMessage', {
                  time: createdAt.format("hh:mm A"),
                  date: createdAt.format("M/D/Y"),
                  interpolation: {'escapeValue': false},
                })}
              </em>
            </Text>
            {generateActionDetailContent(request.actionDetails)}
            {request.lastResolvedBy &&
              <Text fontWeight="light">
                <em>
                  {t('resolvedAtMessage', {
                    time: resolvedAt.format("hh:mm A"),
                    date: resolvedAt.format("M/D/Y"),
                    user: `${request.lastResolvedBy}@`,
                    interpolation: {'escapeValue': false},
                  })}
                </em>
              </Text>
            }

            <Spacer height={16}/>
            <Flex flexDirection="row" justifyContent="left" alignItems="center">
              <StatusIndicator
                status={REQUEST_STATUS_CONFIGS[request.status!].statusIndicator}
                iconAltText={REQUEST_STATUS_CONFIGS[request.status!].statusName}
                messageText={REQUEST_STATUS_CONFIGS[request.status!].statusName}
              />
            </Flex>
          </Col>
        </Card>
      );
    });
  }, [selectedTab, groupedActions]);

  /**
   * Render the currently selected action into a modal
   */
  const modalContent = useMemo(() => {
    if (selectedRequest == undefined) {
      return <></>;
    }
    const createdAt = moment(selectedRequest.createdAt);
    const resolvedAt = moment(selectedRequest.lastResolvedAt);

    return (
      <Modal isOpen={isModalOpen} close={closeModal}>
        <Col
          backgroundColor="neutral0"
          gridGap="S500"
          padding="S500">
          <H3>{t('requestSummary')}</H3>

          <Text>
            {t('requestSubmittedMessage', {
              time: createdAt.format("hh:mm A"),
              date: createdAt.format("M/D/Y"),
              interpolation: {'escapeValue': false},
            })}
          </Text>
          <Text fontWeight="light"><em>{selectedRequest.title}</em></Text>

          {generateActionDetailContent(selectedRequest.actionDetails)}

          {selectedRequest.lastResolvedBy &&
            <Text>
              {t('requestResolutionMessage', {
                user: `${selectedRequest.lastResolvedBy}@`,
                time: resolvedAt.format("hh:mm A"),
                date: resolvedAt.format("M/D/Y"),
                interpolation: {'escapeValue': false},
              })}
            </Text>
          }

          <Flex flexDirection="row-reverse">
            <Button onClick={closeModal} variant={ButtonVariant.Primary}>
              {t('done')}
            </Button>
          </Flex>
        </Col>
      </Modal>
    );
  }, [selectedRequest, isModalOpen]);

  return(
    <>
      {modalContent}
      <Flex flexDirection="row" justifyContent="center" alignItems="center">
        <View width="70%">
          <Flex flexDirection="row" justifyContent="space-between">
            <H1>{requestHelpEnabled ? t('requestSupport') : t('faqs')}</H1>
            <Button data-cy="home-btn" onClick={()=>{navigate('/')}}>{t('home')}</Button>
          </Flex>
          <Spacer height={32}/>

          {requestHelpEnabled &&
            <>
              {isLoading &&
                <Flex flexDirection="row" justifyContent="center" alignItems="center">
                  <Spinner showText loadingText={t('loadingRequests')} />
                </Flex>
              }
              {!isLoading &&
                <>
                  <RequestHelpWorkflowRenderer onRequestCreated={populateRequests} openActionTitles={openActionTitles} />

                  <Spacer height={32}/>

                  <H4>{t('myRequests')}</H4>
                  <Spacer height={16}/>

                  <Flex flexDirection="row" gridGap="S300">
                    <Chip
                      colorScheme={backgroundForTab(RequestTab.OPEN)}
                      onClick={() => setSelectedTab(RequestTab.OPEN)}
                    >
                      {`${t('open')} (${groupedActions.openActions.length})`}
                    </Chip>
                    <Chip
                      colorScheme={backgroundForTab(RequestTab.CLOSED)}
                      onClick={() => setSelectedTab(RequestTab.CLOSED)}
                    >
                      {`${t('closed')} (${groupedActions.closedActions.length})`}
                    </Chip>
                    <Chip
                      colorScheme={backgroundForTab(RequestTab.EXPIRED)}
                      onClick={() => setSelectedTab(RequestTab.EXPIRED)}
                    >
                      {`${t('expired')} (${groupedActions.expiredActions.length})`}
                    </Chip>
                    <Chip
                      colorScheme={backgroundForTab(RequestTab.ALL)}
                      onClick={() => setSelectedTab(RequestTab.ALL)}
                    >
                      {`${t('all')} (${groupedActions.allActions.length})`}
                    </Chip>
                  </Flex>

                  <Spacer height={8}/>

                  <Col gridGap="S200">
                    {actionCards}
                  </Col>
                </>
              }

              <Spacer height={32}/>
            </>
          }

          <H4>{t('frequentlyAskedQuestions')}</H4>
          <Spacer height={16}/>

          <Expander
            titleText={t('feedbacksAvailableQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('FeedbacksAvailableQuestion');
                }
              }
            }
          >
            <Text>{t('feedbacksAvailableAnswer')}</Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('workItemQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('WorkItemQuestion');
                }
              }
            }
          >
            <Text>{t('workItemAnswer')}</Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('percentileQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('PercentileQuestion');
                }
              }
            }
          >
            <Text>{t('percentileAnswer')}</Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('learningCurveFamilyQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('LearningCurveFamilyQuestion');
                }
              }
            }
          >
            <Text>{t('learningCurveFamilyAnswer')}</Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('learningCurveLevelQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('LearningCurveLevelQuestion');
                }
              }
            }
          >
            <Text>{t('learningCurveLevelPartialAnswer')}</Text>
            <Text>
              <ul>
                <li>{`${t('level')} 1: 0-40 ${t('hours')}`}</li>
                <li>{`${t('level')} 2: 40-80 ${t('hours')}`}</li>
                <li>{`${t('level')} 3: 80-120 ${t('hours')}`}</li>
                <li>{`${t('level')} 4: 120-160 ${t('hours')}`}</li>
                <li>{`${t('level')} 5: 160+ ${t('hours')}`}</li>
              </ul>
            </Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('uphQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('UPHQuestion');
                }
              }
            }
          >
            <Text>{t('uphAnswer')}</Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('currentDayPerformanceQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('CurrentDayPerformanceQuestion');
                }
              }
            }
          >
            <Text>{t('currentDayPerformanceAnswer')}</Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('dpmoQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('DPMOQuestion');
                }
              }
            }
          >
            <Text>{t('dpmoAnswer')}</Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('twoFactorAuthQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('TwoFactorAuthQuestion');
                }
              }
            }
          >
            <Text>{t('twoFactorAuthAnswer')}</Text>
          </Expander>
          <Spacer height={16}/>
          <Expander
            titleText={t('dataMissingQuestion')}
            onToggle={
              (isExpanded: boolean) => {
                if (isExpanded) {
                  visitFAQItem('DataMissingQuestion');
                }
              }
            }
          >
            <Text>{t('dataMissingPartialAnswer')}</Text>
            <Text>
              <ol type="a">
                <li>{t('dataMissingPartialSubItem1')}</li>
                <li>{t('dataMissingPartialSubItem2')}</li>
                <li>{t('dataMissingPartialSubItem3')}</li>
              </ol>
            </Text>
            <Text>{t('dataMissingNote')}</Text>
          </Expander>
        </View>
      </Flex>
    </>
  );
}
